import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { APP_ROUTES } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from '../environments/environment';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { APP_CONSTANTS } from './app.constant';
import { CONSTANTS_TOKEN, Language, LocalStorage, SessionService } from '@educare/shared';
import { firstValueFrom } from 'rxjs';
import { RedirectLoginService } from './core/services/redirect-login.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(APP_ROUTES),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      RecaptchaV3Module,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpTranslateLoader,
          deps: [HttpClient]
        }
      }),
      NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' })
    ),
    { provide: CONSTANTS_TOKEN, useValue: APP_CONSTANTS },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchSiteKey },
    MsalService,
    MsalBroadcastService,
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const session = inject(SessionService);
      const redirectLogin = inject(RedirectLoginService);

      const language = session.getItem<string>(LocalStorage.CurrentLanguage) ?? Language.English;
      translate.addLangs([Language.English, Language.Arabic]);
      translate.setDefaultLang(Language.English);
      await firstValueFrom(translate.use(language)).catch(() => undefined);
      session.setItem(LocalStorage.CurrentLanguage, language);
      document.body.classList.toggle('arabic-translation-div', language === Language.Arabic);

      await redirectLogin.completeRedirectLogin();
    }),
    { provide: LocationStrategy, useClass: HashLocationStrategy }
  ]
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.authority,
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
